import HeroCarousel from "@/pages/home/components/HeroCarousel";
import HomeSearch from "@/pages/home/components/HomeSearch";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import { getHot, getLatest } from "@/utils/movie";
import { siteInfo } from "@/mocks/site";
import { useSEO } from "@/hooks/useSEO";

export default function Home() {
  useSEO({
    title: "欧美一级视频－欧美影视精选与资源导航",
    description:
      "欧美一级视频网站提供丰富的欧美电影和电视剧资源，涵盖多种类型和最新作品，方便用户查找和了解欧美影视内容。",
    keywords: "欧美一级视频,欧美影视,欧美电影,欧美电视剧,影视资源,欧美视频大全,欧美影视推荐",
  });

  const latest = getLatest(10);
  const hot = getHot(10);

  return (
    <div className="flex w-full flex-col">
      <HeroCarousel />
      <HomeSearch />

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
        <SectionHeading
          title="分类快捷入口"
          subtitle="按类型、地区与年份快速进入对应影视目录"
        />
        <CategoryGrid />
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6 md:pt-16">
        <SectionHeading
          title="最新上线"
          subtitle="近期更新与上线的欧美影视作品"
          moreTo="/category/movie"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="mt-12 w-full bg-background-100 py-12 md:mt-16 md:py-16">
        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <SectionHeading
            title="热门影视"
            subtitle="当前浏览量与人气最高的精选作品"
            moreTo="/category/movie?sort=hot"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {hot.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <span className="flex w-fit items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
              <i className="ri-information-line"></i>
              关于本站
            </span>
            <h1 className="text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              欧美一级视频
              <span className="mt-1 block text-lg font-semibold text-primary-600 md:text-2xl">
                欧美影视精选与资源导航
              </span>
            </h1>
            <p className="text-sm leading-relaxed text-foreground-700 md:text-base">
              {siteInfo.intro}
            </p>
            <div className="grid grid-cols-3 gap-4 border-t border-background-200 pt-5">
              <div className="flex flex-col">
                <span className="font-label text-2xl font-black text-primary-600">20+</span>
                <span className="text-xs text-foreground-600">精选作品收录</span>
              </div>
              <div className="flex flex-col">
                <span className="font-label text-2xl font-black text-primary-600">8</span>
                <span className="text-xs text-foreground-600">覆盖国家地区</span>
              </div>
              <div className="flex flex-col">
                <span className="font-label text-2xl font-black text-primary-600">12</span>
                <span className="text-xs text-foreground-600">影视类型分类</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 h-[200px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[240px]">
              <img
                src="https://readdy.ai/api/search-image?query=Cinematic%20artistic%20collage%20of%20film%20elements:%20vintage%20clapperboard%2C%20film%20reels%2C%20projector%20light%20beam%20and%20warm%20orange%20spotlight%20on%20a%20deep%20navy%20blue%20background%2C%20moody%20studio%20lighting%2C%20premium%20editorial%20composition%2C%20highly%20detailed%2C%20no%20text%2C%20no%20watermark&width=800&height=480&seq=home-about-01&orientation=landscape"
                alt="影视资料导航氛围图"
                title="欧美一级视频 影视资料导航"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="h-[150px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[180px]">
              <img
                src="https://readdy.ai/api/search-image?query=Row%20of%20classic%20cinema%20seats%20lit%20by%20a%20warm%20projector%20beam%20in%20a%20dark%20auditorium%2C%20deep%20navy%20and%20amber%20tones%2C%20cinematic%20moody%20atmosphere%2C%20soft%20dust%20particles%20in%20light%2C%20premium%20editorial%20photography%2C%20no%20text%2C%20no%20watermark&width=600&height=600&seq=home-about-02&orientation=squarish"
                alt="影院观影氛围图"
                title="欧美影视推荐 观影氛围"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="h-[150px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[180px]">
              <img
                src="https://readdy.ai/api/search-image?query=Stack%20of%20film%20canisters%20and%20a%20notebook%20on%20a%20wooden%20desk%20beside%20a%20warm%20lamp%2C%20deep%20navy%20and%20amber%20color%20palette%2C%20cinematic%20archival%20mood%2C%20soft%20shadows%2C%20premium%20editorial%20photography%2C%20highly%20detailed%2C%20no%20text%2C%20no%20watermark&width=600&height=600&seq=home-about-03&orientation=squarish"
                alt="影视资料整理氛围图"
                title="欧美电影资料导航"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}